import React, { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  CheckCircle, 
  Home, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  ArrowRight,
  Star
} from 'lucide-react';
import { trackBookingConfirmed } from '../utils/analytics';

/**
 * Booking Confirmation Page
 * 
 * This page serves as a landing page after guests complete their booking on Uplisting.
 * It tracks conversions via Google Analytics/GTM and provides helpful next steps.
 * 
 * HOW IT WORKS:
 * 1. Configure Uplisting to redirect to: https://capitol-view-stay.com/booking-confirmation?ref=BOOKING_ID&property=PROPERTY_NAME
 * 2. Or use as a thank-you page when guests manually return after booking
 * 3. The page fires a 'booking_confirmed' event to GTM/GA4 for conversion tracking
 * 
 * URL Parameters:
 * - ref: Booking reference ID (optional, from Uplisting)
 * - property: Property name (optional, for display)
 */
const BookingConfirmation = () => {
  const [searchParams] = useSearchParams();
  const bookingRefParam = searchParams.get('ref');
  const bookingRef = bookingRefParam || 'DIRECT-' + Date.now();
  const propertyName = searchParams.get('property') || 'Capitol View Stays Property';
  const eventFired = useRef(false);

  // Fire conversion tracking event once on mount
  useEffect(() => {
    if (!eventFired.current) {
      eventFired.current = true;
      trackBookingConfirmed({
        bookingId: bookingRef,
        propertyName: propertyName,
        source: 'uplisting'
      });
    }
  }, [bookingRef, propertyName]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section with Success Message */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Booking Confirmed!
          </h1>
          <p className="text-lg md:text-xl text-slate-300 mb-6">
            Thank you for booking with Capitol View Stays. We're excited to host you in Washington DC!
          </p>
          {bookingRefParam && (
            <div className="bg-slate-700/50 rounded-lg inline-block px-6 py-3">
              <p className="text-sm text-slate-400">Booking Reference</p>
              <p className="text-xl font-mono font-bold text-amber-400">{bookingRef}</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* What's Next Section */}
        <Card className="mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">What's Next?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-500 text-slate-900 rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="font-semibold text-slate-900">Check Your Email</h3>
                  <p className="text-slate-600">You'll receive a confirmation email with all the details about your stay, including check-in instructions.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-500 text-slate-900 rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="font-semibold text-slate-900">Save Our Contact Info</h3>
                  <p className="text-slate-600">Keep our phone number handy for any questions before or during your stay.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-500 text-slate-900 rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="font-semibold text-slate-900">Explore Our Local Guide</h3>
                  <p className="text-slate-600">Check out our curated recommendations for restaurants, attractions, and things to do in DC.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Need Help?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <a href="tel:+15715075650" className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Call or Text</p>
                  <p className="font-semibold text-slate-900">(571) 507-5650</p>
                </div>
              </a>
              <a href="mailto:capitolviewstays@gmail.com" className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email Us</p>
                  <p className="font-semibold text-slate-900">capitolviewstays@gmail.com</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Helpful Links */}
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/local-guide" className="block">
            <Card className="h-full hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Local Guide</h3>
                <p className="text-sm text-slate-600 mb-4">Restaurants, attractions & tips</p>
                <span className="inline-flex items-center text-amber-600 font-medium text-sm">
                  Explore <ArrowRight className="h-4 w-4 ml-1" />
                </span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/house-rules" className="block">
            <Card className="h-full hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Calendar className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">House Rules</h3>
                <p className="text-sm text-slate-600 mb-4">Check-in times & policies</p>
                <span className="inline-flex items-center text-amber-600 font-medium text-sm">
                  Review <ArrowRight className="h-4 w-4 ml-1" />
                </span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/" className="block">
            <Card className="h-full hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Home className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-2">Our Properties</h3>
                <p className="text-sm text-slate-600 mb-4">Browse all our rentals</p>
                <span className="inline-flex items-center text-amber-600 font-medium text-sm">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </span>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Review Request - Hidden until Google Business ID is configured */}
        {/* 
        TODO: Replace YOUR_GOOGLE_BUSINESS_ID with the actual Google Business Profile ID
        To get your ID: Go to Google Business Profile > Get more reviews > Copy the review link
        */}
        {/* <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-6 md:p-8 text-center">
            <Star className="h-10 w-10 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Enjoyed Your Stay?</h2>
            <p className="text-slate-600 mb-4">
              We'd love to hear about your experience! Your review helps other travelers discover Capitol View Stays.
            </p>
            <a 
              href="https://g.page/r/YOUR_GOOGLE_BUSINESS_ID/review" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                Leave a Review
              </Button>
            </a>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
};

export default BookingConfirmation;
